<template>
  <div>
    <financial-adjustment-details-sidebar />
    <!-- Totalizer -->
    <financial-adjustments-totalizer
      :saveAction="search"
      :totalizer="totalizer"
    />
    <unread-financial-batches-strip />
    <b-card no-body class="mb-0">
      <!-- Filters -->
      <financial-adjustments-list-filters ref="filters" />
      <div class="m-2">
        <!-- Table Top -->
        <b-row>
          <!-- Per Page -->
          <b-col
            cols="12"
            md="6"
            class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
          >
            <label class="mb-0">Exibir</label>
            <v-select
              v-model="itemsPerPage"
              dir="ltr"
              :options="itemsPerPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block mx-50"
              @input="search(currentPage)"
            >
              <template #no-options="{ search, searching, loading }">
                Sem resultados
              </template>
            </v-select>
            <label class="mb-0">itens</label>
          </b-col>
          <!-- Search -->
          <b-col cols="12" md="6">
            <div class="d-flex align-items-center justify-content-end">
              <b-button
                variant="outline-secondary"
                @click.prevent="clearSearch"
                class="mr-1"
                :disabled="loading"
              >
                Limpar filtros
              </b-button>
              <b-button
                variant="primary"
                @click.prevent="search(1)"
                :disabled="loading"
              >
                <b-spinner v-if="loading" small />
                <feather-icon v-else icon="SearchIcon" class="mr-50" />
                <span>
                  {{ loading ? "Aguarde..." : "Pesquisar" }}
                </span>
              </b-button>
            </div>
          </b-col>
        </b-row>
      </div>
      <b-table
        hover
        class="position-relative"
        :items="items"
        responsive
        :fields="tableColumns"
        primary-key="id"
        show-empty
        empty-text="Não foram encontrados ajustes para esta busca"
        :busy="loading"
        :no-local-sorting="true"
        :sort-by="sortField"
        @sort-changed="sort"
      >
        <!-- Column: Nome do lançamento -->
        <template #cell(name)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">{{
              data.item.name
            }}</span>
            <!--
              A referência do lote entra ABAIXO da descrição, e não como coluna própria: a tabela
              já tem onze colunas e rola na horizontal. E a ausência do badge também informa —
              lançamento manual não tem lote.

              Clicar refiltra a lista por essa referência. É o mesmo recorte que o link do
              detalhe e o do e-mail usam, e é o que substitui a tela de lotes que foi cortada.
            -->
            <div v-if="data.item.batch_reference" class="mt-25">
              <b-badge
                variant="light-secondary"
                class="cursor-pointer"
                :title="`Ver os lançamentos de ${data.item.batch_reference}`"
                :data-testid="`adjustment-batch-${data.item.id}`"
                @click="filterByBatch(data.item.batch_reference)"
              >
                {{ data.item.batch_reference }}
              </b-badge>
            </div>
          </div>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">{{
              data.item.status | adjustmentStatus
            }}</span>
          </div>
        </template>

        <!-- Column: Serviços/ Colaboradores/ Comercial -->
        <template #cell(subject)="data">
          <div class="text-wrap">
            <span class="align-text-top text-capitalize">{{
              data.item.consultant_name || data.item.department_name
            }}</span>
          </div>
        </template>

        <!-- Column: Valor -->
        <template #cell(value)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.value | toCurrency
            }}</span>
          </div>
        </template>

         <!-- Column: Valor -->
         <template #cell(installment)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.installment
            }}</span>
          </div>
        </template>

        <!-- Column: Data da solicitação -->
        <template #cell(launch_date)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.date | dateToStr }}</span>
          </div>
        </template>

        <!-- Column: Tipo do lançamento -->
        <template #cell(credit_type)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{ data.item.credit_type }}</span>
          </div>
        </template>

        <!-- Column: Motivo do lançamento -->
        <template #cell(adjustment_reason)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.adjustment_reason
            }}</span>
          </div>
        </template>

        <!-- Column: Empresa pagadora -->
        <template #cell(paying_company)="data">
          <div class="text-wrap">
            <b-badge
              v-if="data.item.paying_company_id"
              :variant="payingCompanyVariant(data.item.paying_company_id)"
              :data-testid="`adjustment-paying-company-${data.item.id}`"
            >
              {{ data.item.paying_company_name }}
            </b-badge>
            <span
              v-else
              class="text-muted"
              :data-testid="`adjustment-paying-company-${data.item.id}`"
            >
              Sem empresa pagadora
            </span>
          </div>
        </template>

        <!-- Column: Data de faturamento -->
        <template #cell(billed_at)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.billed_at | dateTime
            }}</span>
          </div>
        </template>

         <!-- Column: Data de faturamento -->
        <template #cell(approved_at)="data">
          <div class="text-wrap">
            <span class="align-text-top">{{
              data.item.approved_at | dateTime
            }}</span>
          </div>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-button-group>
            <b-button
              v-if="data.item.invoice_id || 
                !(canUpdateStatus && data.item.status == adjustmentStatus.PENDING_STATUS)
              "
              :id="`details-adjustment` + data.item.id"
              class="btn-icon"
              variant="flat"
              style="padding: 0; color: white"
              @click="openDetails(data.item.id)"
            >
              <feather-icon class="text-body" icon="FileTextIcon" />
            </b-button>
            <b-tooltip
              :target="`details-adjustment` + data.item.id"
              triggers="hover"
            >
              Detalhes do ajuste
            </b-tooltip>
            <template v-if="!data.item.invoice_id">
              <template v-if="canUpdateStatus && data.item.status == adjustmentStatus.PENDING_STATUS">
                <b-button
                  :id="`update-status-adjustment` + data.item.id"
                  class="btn-icon"
                  variant="flat"
                  style="padding: 0; color: white"
                  @click="openDetails(data.item.id)"
                >
                  <feather-icon class="text-body" icon="CheckSquareIcon" />
                </b-button>
                <b-tooltip
                  :target="`update-status-adjustment` + data.item.id"
                  triggers="hover"
                >
                  Avaliar ajuste
                </b-tooltip>
              </template>
              <b-button
                v-if="canUpdate"
                :id="`edit-adjustment` + data.item.id"
                class="btn-icon mx-1"
                variant="flat"
                style="padding: 0; color: white"
                @click="editAdjustment(data.item.id)"
              >
                <feather-icon class="text-body" icon="EditIcon" />
              </b-button>
              <b-tooltip
                v-if="canUpdate"
                :target="`edit-adjustment` + data.item.id"
                triggers="hover"
              >
                Alterar ajuste
              </b-tooltip>
              <b-button
                v-if="canDelete"
                :id="`delete-adjustment` + data.item.id"
                class="btn-icon"
                variant="flat"
                style="padding: 0; color: white"
                @click="deleteAdjustment(data.item.id)"
              >
                <feather-icon class="text-body" icon="TrashIcon" />
              </b-button>
              <b-tooltip
                v-if="canDelete"
                :target="`delete-adjustment` + data.item.id"
                triggers="hover"
              >
                Remover ajuste
              </b-tooltip>
            </template>
            <!--
              Faturado trava avaliar, alterar e remover — e ate aqui a unica pista disso era a
              coluna ficar mais curta, o que se le como falta de permissao. O rotulo diz o motivo.
              E a regra "nao se mexe no que ja foi faturado": o ajuste existe para arrumar o
              passado no presente, nao para reescreve-lo.
            -->
            <small
              v-else
              class="text-muted d-inline-flex align-items-center"
              :data-testid="`billed-adjustment` + data.item.id"
            >
              <feather-icon icon="LockIcon" size="13" class="mr-25" />
              Faturado
            </small>
          </b-button-group>
        </template>
      </b-table>
      <div class="mx-2 mb-2">
        <b-row>
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-start"
          >
            <span class="text-muted"
              >Exibindo {{ pageItemsFrom }} à {{ pageItemsTo }} de
              {{ totalItems }} itens</span
            >
          </b-col>
          <!-- Pagination -->
          <b-col
            cols="12"
            sm="6"
            class="d-flex align-items-center justify-content-center justify-content-sm-end"
          >
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="itemsPerPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
              prev-class="prev-item"
              next-class="next-item"
              @change="search"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BCardHeader,
  BCardBody,
  BSpinner,
  BButtonGroup,
  BTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import { mapActions, mapGetters } from "vuex";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent";
import FinancialAdjustmentsListFilters from "../components/FinancialAdjustmentsListFilters";
import UnreadFinancialBatchesStrip from "@/modules/financial_batch/components/UnreadFinancialBatchesStrip";
import FinancialAdjustmentsTotalizer from "../components/FinancialAdjustmentsTotalizer";
import FinancialAdjustmentDetailsSidebar from "../components/FinancialAdjustmentDetailsSidebar";
import { getRangeDates } from "@/helpers/date_picker";
import { ORIGIN_ALL } from "@/constants/financial_adjustment_origins";
import * as sharedTypes from "@/modules/shared/store/types";
import * as types from "../store/types";
import useAppConfig from "@core/app-config/useAppConfig";
import {
  CREATE_ACTION,
  UPDATE_ACTION,
  DELETE_ACTION,
  FINANCIAL_ADJUSTMENTS,
  MANAGE_ACTION,
  FINANCIAL_ADJUSTMENT_APPROVAL
} from "@/constants/resources";
import * as adjustmentStatus from "@/constants/financial_adjustments";
import { PLATFORM_ID } from "@/constants/paying_companies";


export default {
  components: {
    UnreadFinancialBatchesStrip,
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BSpinner,
    BButtonGroup,
    BTooltip,
    vSelect,
    FinancialAdjustmentsListFilters,
    FinancialAdjustmentsTotalizer,
    FinancialAdjustmentDetailsSidebar,
  },
  setup() {
    const { skin } = useAppConfig();

    return { toast: useToast(), skin };
  },
  data() {
    return {
      adjustmentStatus,
      loading: false,
      tableColumns: [
        { key: "name", label: "Descrição", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "subject", label: "Interessado", sortable: true },
        { key: "value", label: "Valor", sortable: true },
        { key: "installment", label: "Parcela", sortable: true },
        { key: "launch_date", label: "Data da solicitação", sortable: true },
        { key: "credit_type", label: "Tipo do lançamento", sortable: true },
        {
          key: "adjustment_reason",
          label: "Motivo do lançamento",
          sortable: true,
        },
        // ⚠️ Sem `sortable`: a coluna vem de um alias do join com `paying_companies` e
        // `scopeOrder` joga a chave crua num `orderByRaw`. Ordenar por ela mandaria um
        // nome de coluna inexistente para o MySQL e derrubaria a listagem inteira.
        { key: "paying_company", label: "Empresa pagadora" },
        { key: "billing_month", label: "Mês da fatura", sortable: true },
        { key: "billed_at", label: "Data de faturamento", sortable: true },
        { key: "approved_at", label: "Aprovado em", sortable: true },
        { key: "actions", label: "Ações" },
      ],
      order: undefined,
      sortField: null,
      currentPage: 1,
      itemsPerPage: 1,
    };
  },
  computed: {
    ...mapGetters({
      searchResult: types.FINANCIAL_ADJUSTMENTS_SEARCH_RESULT,
      totalizer: types.FINANCIAL_ADJUSTMENT_TOTALIZER,
      initialItemsPerPage: sharedTypes.INITIAL_ITEMS_PER_PAGE,
      itemsPerPageOptions: sharedTypes.ITEMS_PER_PAGE_OPTIONS,
    }),
    canCreate() {
      return this.$can(CREATE_ACTION, FINANCIAL_ADJUSTMENTS);
    },
    canUpdate() {
      return this.$can(UPDATE_ACTION, FINANCIAL_ADJUSTMENTS);
    },
    canDelete() {
      return this.$can(DELETE_ACTION, FINANCIAL_ADJUSTMENTS);
    },
    canUpdateStatus() {
      return this.$can(MANAGE_ACTION, FINANCIAL_ADJUSTMENT_APPROVAL);
    },
    items: function () {
      return this.searchResult?.data || [];
    },
    totalItems: function () {
      return this.searchResult?.total || 0;
    },
    pageItemsFrom: function () {
      return this.searchResult?.from || 0;
    },
    pageItemsTo: function () {
      return this.searchResult?.to || 0;
    },
  },
  mounted() {
    this.itemsPerPage = this.initialItemsPerPage;
    this.search();
  },
  methods: {
    ...mapActions({
      searchFinancialAdjustments: types.SEARCH_FINANCIAL_ADJUSTMENTS,
      getTotalizer: types.GET_FINANCIAL_ADJUSTMENT_TOTALIZER,
      openDetailsSidebar: types.OPEN_FINANCIAL_ADJUSTMENT_DETAILS_SIDEBAR,
      deleteFinancialAdjustment: types.DELETE_FINANCIAL_ADJUSTMENT,
      openEditFinancialAdjustmentSidebar:
        types.OPEN_SAVE_FINANCIAL_ADJUSTMENT_SIDEBAR,
    }),
    search(currentPage) {
      this.loading = true;
      this.currentPage = Number.isInteger(currentPage) ? currentPage : 1;
      const {
        creditType,
        consultant,
        employee,
        department,
        adjustmentReason,
        rangeDate,
        status,
        payingCompany,
        origin,
      } = this.$refs.filters;
      const date = getRangeDates(rangeDate);
      // ⚠️ `searchRules` valida `paying_company_id` como `nullable|string` — é assim que a
      // sentinela 'none' (sem empresa pagadora) trafega no mesmo campo que os ids. Um id
      // numérico cru reprovaria na validação e a busca voltaria 400.
      const payingCompanyId =
        payingCompany == null ? undefined : String(payingCompany);
      this.searchFinancialAdjustments({
        credit_type: creditType,
        consultant,
        employee,
        department,
        status,
        adjustment_reason: adjustmentReason,
        paying_company_id: payingCompanyId,
        origin,
        // Chega só por link, vindo do rastro no detalhe do ajuste: não há controle na tela.
        batch_reference: this.$route.query.batch_reference || undefined,
        launch_date_start: date.start,
        launch_date_end: date.end,
        order: this.order,
        limit: this.itemsPerPage,
        currentPage: this.currentPage
      })
        .catch((error) => {
          this.toast({
            component: ToastificationContent,
            props: {
              title: "Oops!",
              text: "Ocorreu um erro ao buscar os ajustes. Entre em contato com o setor de TI.",
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        })
        .finally(() => {
          this.loading = false;
        });
      this.getTotalizer({
        credit_type: creditType,
        consultant,
        employee,
        department,
        adjustment_reason: adjustmentReason,
        // O totalizador precisa do MESMO filtro da tabela: um totalizador somando os dois
        // CNPJs sobre uma tabela filtrada por um só é a divergência que o Financeiro
        // reconcilia na mão depois.
        //
        // ⚠️ `status` e `origin` faltavam aqui, contra o que este comentário já prometia: os
        // cards somavam o recorte inteiro enquanto a tabela abaixo mostrava um pedaço dele.
        status,
        paying_company_id: payingCompanyId,
        origin,
        batch_reference: this.$route.query.batch_reference || undefined,
        launch_date_start: date.start,
        launch_date_end: date.end,
      }).catch((error) => {
        this.toast({
          component: ToastificationContent,
          props: {
            title: "Oops!",
            text: "Ocorreu um erro ao recuperar os dados do totalizador. Entre em contato com o setor de TI.",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
    },
    // ⚠️ Nesta tela success e danger já significam crédito e débito. O CNPJ usa
    // secondary/info para não ser lido como sinal do valor.
    /**
     * Refiltra a lista pela referência do lote.
     *
     * `catch(() => {})` no replace é o padrão já usado no `clearSearch` desta tela: navegar para
     * a mesma rota com query diferente rejeita a promise no vue-router 3, e a rejeição não
     * tratada vira erro de console sem nada de errado ter acontecido.
     */
    filterByBatch(reference) {
      this.$router
        .replace({ query: { ...this.$route.query, batch_reference: reference } })
        .catch(() => {});
      this.search(1);
    },
    payingCompanyVariant(id) {
      return Number(id) === PLATFORM_ID ? "light-info" : "light-secondary";
    },
    sort(event) {
      //clear in third click
      if (!event.sortDesc && this.sortField == event.sortBy) {
        this.order = undefined;
        this.sortField = null;
      } else {
        var direction = event.sortDesc ? " DESC" : " ASC";
        this.order = event.sortBy + direction;
        this.sortField = event.sortBy;
      }
      this.search();
    },
    openDetails(id) {
      this.openDetailsSidebar({ 
        id,
        saveAction: () => {
          this.search(this.currentPage);
        }, 
      });
    },
    deleteAdjustment(id) {
      this.$swal({
        title: "Confirmação",
        text: `Deseja realmente remover? A operação não poderá ser desfeita.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Remover",
        cancelButtonText: "Cancelar",
        background: `${this.skin === "dark" ? "#283046" : ""}`,
        customClass: {
          confirmButton: "btn btn-primary",
          cancelButton: "btn btn-secondary ml-1",
          htmlContainer: `${this.skin === "dark" ? "text-white" : ""}`,
        },
      }).then((result) => {
        if (result.value) {
          this.deleteFinancialAdjustment(id)
            .then((response) => {
              this.toast({
                component: ToastificationContent,
                props: {
                  title: "Sucesso!",
                  text: "Removido com sucesso.",
                  icon: "CoffeeIcon",
                  variant: "success",
                },
              });
              this.search(this.currentPage);
            })
            .catch((err) => {
              if (err.response.status == 405) {
                this.toast({
                  component: ToastificationContent,
                  props: {
                    title: "Oops!",
                    text:
                      "Ocorreu um erro ao remover. " +
                      err.response.data.message,
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                });
              } else {
                this.toast({
                  component: ToastificationContent,
                  props: {
                    title: "Oops!",
                    text: "Ocorreu um erro ao remover. Entre em contato com o setor de TI.",
                    icon: "AlertTriangleIcon",
                    variant: "danger",
                  },
                });
              }
            });
        }
      });
    },
    editAdjustment(id) {
      this.openEditFinancialAdjustmentSidebar({ id, saveAction: this.search });
    },
    clearSearch() {
      this.$refs.filters.creditType = undefined;
      this.$refs.filters.consultant = undefined;
      this.$refs.filters.employee = undefined;
      this.$refs.filters.department = undefined;
      this.$refs.filters.adjustmentReason = undefined;
      this.$refs.filters.rangeDate = undefined;
      this.$refs.filters.payingCompany = undefined;
      this.$refs.filters.origin = ORIGIN_ALL;
      // A URL pode carregar os filtros do link de origem (memória de cálculo da nota,
      // "Total Futuro Hub"). Sem limpar a query, "Limpar filtros" some com os campos mas um F5
      // traria tudo de volta — o usuário acharia que o botão não funciona.
      //
      // ⚠️ `status` continua fora do clear de propósito: é o default de quem aprova ajustes
      // (`canUpdateStatus`), não um filtro que veio do link.
      if (Object.keys(this.$route.query).length) {
        this.$router
          .replace({ name: this.$route.name, query: {} })
          .catch(() => {});
      }
      this.search(1);
    },
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
